import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import UserSignedIn from "./UserSignedIn";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { Search } from "lucide-react";
import MobileBurgerMenu from "./MobileBurgerMenu";

function Navbar() {
  const { currentUser } = useAuth();
  const [isFetched, setIsFetched] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [isScrolled, setIsScrolled] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const navigate = useNavigate();

  const quickSearches = [
    "Gastritis-Friendly",
    "IBS-Friendly",
    "GERD-Friendly",
    "Barretts-Esophagus-Friendly",
  ];

  const searchRecipe = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q === "" && selectedCategories.size === 0) {
      message.info("Enter a keyword or pick a category to search");
      return;
    }

    // Close mobile search if open
    setShowMobileSearch(false);

    const params = new URLSearchParams();
    if (q) params.set("search_query", q);
    if (selectedCategories.size > 0)
      params.set("category", Array.from(selectedCategories).join(","));
    params.set("calorieTotal", "0 TO 50000");

    navigate(`/search?${params.toString()}`);
  };

  const toggleCategory = (category) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  useEffect(() => {
    if (currentUser !== undefined) setIsFetched(true);
  }, [currentUser]);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 150);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  function handleClick(e) {
    e.preventDefault();
    if (isFetched) navigate("/sign_up");
  }

  function handleLoginClick(e) {
    e.preventDefault();
    if (isFetched) navigate("/login");
  }

  if (currentUser) {
    return <UserSignedIn user={currentUser} />;
  }

  // Heights for navbar + spacer (prevents content overlap)
  const spacerClass = isScrolled
    ? "h-16 sm:h-18 md:h-20"
    : "h-20 sm:h-22 md:h-24";

  return (
    <>
      <nav
        className={[
          "fixed inset-x-0 top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur transition-all duration-300",
          isScrolled ? "shadow-md" : "shadow-sm",
          "pt-[max(env(safe-area-inset-top),0px)]",
        ].join(" ")}
        aria-label="Site Navigation"
      >
        {/* Main navigation */}
        <div className="transition-all duration-300">
          <div
            className={[
              "mx-auto max-w-7xl px-3 sm:px-4 lg:px-8",
              isScrolled ? "py-2" : "py-3 sm:py-4",
            ].join(" ")}
          >
            {/* Mobile Top Bar - Logo, Menu, Search Toggle */}
            <div className="flex items-center justify-between md:hidden">
              <div className="flex items-center gap-2">
                {/* Mobile: full menu trigger/list */}
                <div className="md:hidden">
                  <MobileBurgerMenu />
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/")}
                  className="group flex items-center gap-2"
                  aria-label="Go to homepage"
                >
                  <div className="text-left">
                    <h1
                      className={[
                        "font-bold tracking-tight text-rose-500 transition-all",
                        isScrolled ? "text-lg" : "text-xl",
                      ].join(" ")}
                    >
                      Gentle Gourmet
                    </h1>
                    <p
                      className={[
                        "text-slate-500 transition-all",
                        isScrolled ? "text-[10px]" : "text-xs",
                      ].join(" ")}
                    >
                      Recipes for Every Kitchen
                    </p>
                  </div>
                </button>
              </div>

              <div className="flex items-center gap-2">
                {/* Mobile Search Toggle */}
                <button
                  type="button"
                  onClick={() => setShowMobileSearch(!showMobileSearch)}
                  className="p-2 text-slate-600 hover:text-slate-800"
                  aria-label="Toggle search"
                >
                  <Search size={20} />
                </button>

                {/* Mobile Auth Buttons */}
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={handleLoginClick}
                    className="rounded-full px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    Sign In
                  </button>
                </div>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden md:grid md:grid-cols-[auto_1fr_auto] md:items-center md:gap-6">
              {/* Logo */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/")}
                  className="group flex items-center gap-2"
                  aria-label="Go to homepage"
                >
                  <div className="text-left">
                    <h1
                      className={[
                        "font-bold tracking-tight text-rose-500 transition-all",
                        isScrolled ? "text-lg leading-5" : "text-2xl leading-7",
                      ].join(" ")}
                    >
                      Gentle Gourmet
                    </h1>
                    <p
                      className={[
                        "text-slate-500 transition-all",
                        isScrolled ? "text-[11px]" : "text-xs",
                      ].join(" ")}
                    >
                      Recipes for Every Kitchen
                    </p>
                  </div>
                </button>
              </div>

              {/* Search - Desktop */}
              <div className="w-full max-w-xl justify-self-center">
                <form onSubmit={searchRecipe} className="mb-1">
                  <div
                    className={[
                      "relative flex items-center rounded-full border-2 transition-all",
                      "bg-slate-50 border-slate-200 focus-within:bg-white",
                      "focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-500/10",
                      isScrolled ? "h-10" : "h-12",
                    ].join(" ")}
                  >
                    <Search
                      className="absolute left-3 text-slate-400"
                      size={18}
                    />
                    <input
                      type="search"
                      placeholder="Search GERD, IBS, gastritis recipes…"
                      className={[
                        "w-full bg-transparent outline-none rounded-full",
                        "pl-10 pr-28 text-[15px] placeholder:text-slate-400",
                        isScrolled ? "h-10" : "h-12",
                      ].join(" ")}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      aria-label="Search recipes"
                    />
                    <button
                      type="submit"
                      className={[
                        "absolute right-1 rounded-full px-4 font-medium text-white",
                        "bg-blue-600 hover:bg-blue-700 active:bg-blue-800 transition-colors",
                        isScrolled ? "h-8 text-sm" : "h-10 text-sm",
                      ].join(" ")}
                    >
                      Search
                    </button>
                  </div>
                </form>

                {/* Quick searches */}
                <div
                  className={[
                    "flex flex-wrap items-center gap-2",
                    isScrolled ? "mt-1" : "mt-1.5",
                  ].join(" ")}
                >
                  <span className="text-[11px] font-medium text-slate-500 hidden sm:inline">
                    Popular:
                  </span>
                  {quickSearches.map((s) => {
                    const selected = selectedCategories.has(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => toggleCategory(s)}
                        className={[
                          "rounded-xl border transition-all",
                          "px-2 py-1 sm:px-3 sm:py-1",
                          "text-xs",
                          selected
                            ? "border-blue-600 bg-blue-600 text-white hover:bg-blue-700"
                            : "border-slate-200 text-blue-600 hover:bg-blue-600 hover:text-white",
                        ].join(" ")}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Auth actions - Desktop */}
              <div className="justify-self-end">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleLoginClick}
                    className="rounded-full px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    Sign In
                  </button>
                  <button
                    type="button"
                    onClick={handleClick}
                    className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 active:bg-emerald-800"
                  >
                    Join Now
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile Search Expanded View */}
            {showMobileSearch && (
              <div className="mt-3 md:hidden border-t border-slate-200 pt-3">
                <form onSubmit={searchRecipe} className="mb-3">
                  <div className="relative flex items-center rounded-full border-2 bg-slate-50 border-slate-200 focus-within:bg-white focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-500/10 h-12">
                    <Search
                      className="absolute left-3 text-slate-400"
                      size={18}
                    />
                    <input
                      type="search"
                      placeholder="Search recipes…"
                      className="w-full bg-transparent outline-none rounded-full pl-10 pr-24 h-12 text-[15px] placeholder:text-slate-400"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      aria-label="Search recipes"
                    />
                    <button
                      type="submit"
                      className="absolute right-1 rounded-full h-10 px-4 font-medium text-white bg-blue-600 hover:bg-blue-700 text-sm"
                    >
                      Search
                    </button>
                  </div>
                </form>

                {/* Mobile Quick searches */}
                <div className="flex flex-wrap gap-2">
                  {quickSearches.map((s) => {
                    const selected = selectedCategories.has(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => toggleCategory(s)}
                        className={[
                          "rounded-xl border px-3 py-1.5 text-xs transition-all",
                          selected
                            ? "border-blue-600 bg-blue-600 text-white hover:bg-blue-700"
                            : "border-slate-200 text-blue-600 hover:bg-blue-600 hover:text-white",
                        ].join(" ")}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>

                {/* Mobile Join Button */}
                <div className="mt-3 flex justify-center">
                  <button
                    type="button"
                    onClick={handleClick}
                    className="w-full rounded-full bg-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
                  >
                    Join Now - Find Your Perfect Recipes
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Spacer to prevent content being hidden behind fixed navbar */}
      <div aria-hidden className={spacerClass} />
    </>
  );
}

export default Navbar;
